import { ConfigProvider, Segmented } from "antd";
import { colors } from "shared/theme/color";
import { AnalyticsRange } from "shared/types";
import { translate } from "shared/languages";

interface RangeSegmentedProps {
  value: AnalyticsRange;
  onChange: (value: AnalyticsRange) => void;
  // Pass false when a sibling CustomMonthRangePicker's range is the one
  // actually active — none of these fixed presets apply then, so nothing
  // should LOOK selected (a stale-highlighted preset next to an active custom
  // range is misleading about which one is really driving the report).
  // `value` still has to be a real AnalyticsRange either way (Segmented has
  // no true "nothing selected" state — an undefined/unmatched value falls
  // back to highlighting its first option, which would be equally
  // misleading), so this only fades the highlight, it doesn't unset it.
  // Callers with no custom-range sibling can omit this (defaults to true).
  active?: boolean;
  // Set false on the Compare report specifically: it pairs this with
  // customDayRange/allTime, not customRange, and never wires an "ytd" case
  // into its onChange — an unhandled "ytd" there would send range=ytd
  // straight to the server, which doesn't recognize that token. Every other
  // consumer wires customRange + the ytd branch (see the report pages) and
  // wants the option, so this defaults to true.
  includeYtd?: boolean;
}

// Shared pill-shaped Segmented look — used by every Segmented toggle in the
// Analytics tab (day-range presets here, the all-time/range mode toggle on
// the Compare report) so they all read as the same control family rather
// than each report inventing its own toggle style.
export const pillSegmentedTheme = {
  components: {
    Segmented: {
      trackBg: colors.bgSoft,
      trackPadding: 4,
      itemColor: colors.ink2,
      itemHoverColor: colors.ink,
      itemSelectedBg: colors.card,
      itemSelectedColor: colors.ink,
      borderRadius: 999,
      borderRadiusSM: 999,
    },
  },
  token: {
    boxShadowTertiary: "0 1px 2px rgba(15, 15, 14, 0.10)",
  },
};

// Rolling trailing windows ending today — deliberately not calendar
// Weekly/Monthly/Quarterly/Yearly buckets. A calendar period-to-date (e.g.
// "this month") looks misleadingly near-empty early in the period; a rolling
// window is always a full, consistent size regardless of what day it is.
//
// The two middle presets step back by CALENDAR MONTHS, not by 30/90 days:
// "1 bulan terakhir" on 4 Aug means 4 Jul–4 Aug, which is how an admin reads
// it. The server resolves the same tokens (see analyticsWindowSchema) and
// returns the resolved window as range_label, so the page caption and both
// exports always describe the identical period.
const RangeSegmented = ({
  value,
  onChange,
  active = true,
  includeYtd = true,
}: RangeSegmentedProps) => (
  <ConfigProvider
    theme={
      active
        ? pillSegmentedTheme
        : {
            ...pillSegmentedTheme,
            components: {
              Segmented: {
                ...pillSegmentedTheme.components.Segmented,
                // Blend the "selected" pill into the track so no preset reads
                // as active while a custom range is actually driving the
                // report — see the `active` prop doc above.
                itemSelectedBg: "transparent",
                itemSelectedColor: colors.ink2,
              },
            },
          }
    }
  >
    <Segmented
      value={value}
      onChange={(val) => onChange(val as AnalyticsRange)}
      options={[
        { label: translate("analyticsPage.range.d7"), value: "7" },
        { label: translate("analyticsPage.range.m1"), value: "1m" },
        { label: translate("analyticsPage.range.m3"), value: "3m" },
        { label: translate("analyticsPage.range.m6"), value: "6m" },
        { label: translate("analyticsPage.range.y1"), value: "1y" },
        ...(includeYtd
          ? [{ label: translate("analyticsPage.range.ytd"), value: "ytd" as const }]
          : []),
      ]}
    />
  </ConfigProvider>
);

export default RangeSegmented;
